/** The Templates list: searchable, sorted by name, one page at a time. */
export interface TemplateListItem {
  id: string;
  name: string;
  department: string | null;
  current_version: number;
  updated_at: string;
  tests: number;
  sensitive: boolean;
  /** What the lab charges, in rupees. Private to the lab; null when not set. */
  price: number | null;
  /** The sample the test asks for, as written in the template. */
  sample_type: string | null;
  /** Days from registering to the report being ready; null = the same day. */
  turnaround_days: number | null;
}

function templateWhere(orgId: string, q: string): { where: string; params: unknown[] } {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const search = words.map((_, i) => `t.search_text like $${i + 2}`).join(' and ');
  return {
    where: `t.org_id = $1 and t.deleted_at is null and t.archived_at is null ${search ? `and ${search}` : ''}`,
    params: [orgId, ...words.map((w) => `%${w}%`)],
  };
}

/**
 * SQL for the searchable template list; used as a live query. Pass `page` and `pageSize` for a
 * page of the list (the Templates screen), or only `pageSize` for the first rows (pickers).
 */
export function templateListQuery(
  orgId: string,
  q: string,
  opts: { page?: number; pageSize?: number } = {},
): { sql: string; params: unknown[] } {
  const { where, params } = templateWhere(orgId, q);
  const size = Math.min(Math.max(Math.floor(opts.pageSize ?? 50), 1), 500);
  const offset = (Math.max(Math.floor(opts.page ?? 1), 1) - 1) * size;
  return {
    sql: `select t.id, t.name, t.department, t.current_version, t.updated_at, t.sensitive,
            (select count(*)::int from jsonb_array_elements(v.doc->'rows') r where r->>'kind' = 'test') as tests,
            (v.doc->>'price')::float8 as price, v.doc->>'sampleType' as sample_type,
            (v.doc->>'turnaroundDays')::int as turnaround_days
          from templates t
          join template_versions v on v.template_id = t.id and v.version = t.current_version
          where ${where}
          order by lower(t.name), t.id
          limit ${size} offset ${offset}`,
    params,
  };
}

export function templateCountQuery(orgId: string, q: string): { sql: string; params: unknown[] } {
  const { where, params } = templateWhere(orgId, q);
  return { sql: `select count(*)::int as n from templates t where ${where}`, params };
}
